import { FC, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { balanceService } from "services/balance/balance.service"

interface Props {
  open: boolean
  processor?: string
  payment_method?: string
  onClose: () => void
  onSuccess: () => void
}

export const TopUpModal: FC<Props> = ({ open, processor: fixedProcessor, payment_method: fixedMethod, onClose, onSuccess }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")
  const [loading, setLoading] = useState(false)

  const isSelectable = !fixedProcessor || !fixedMethod

  const [processors, setProcessors] = useState<string[]>([])
  const [methods, setMethods] = useState<string[]>([])
  const [selectedProcessor, setSelectedProcessor] = useState(fixedProcessor ?? "")
  const [selectedMethod, setSelectedMethod] = useState(fixedMethod ?? "")

  const processor = fixedProcessor || selectedProcessor
  const payment_method = fixedMethod || selectedMethod

  useEffect(() => {
    if (open && isSelectable) {
      balanceService.listPaymentRouteProcessors().then((p) => {
        if (p) setProcessors(p)
      })
    }
  }, [open, isSelectable])

  useEffect(() => {
    if (open && isSelectable && selectedProcessor) {
      balanceService.listPaymentRoutes({ processor: selectedProcessor, pageSize: 200 }).then((result) => {
        if (result) {
          const unique = Array.from(new Set(result.data.map((r) => r.payment_method))).sort()
          setMethods(unique)
          setSelectedMethod("")
        }
      })
    }
  }, [open, isSelectable, selectedProcessor])

  const handleSubmit = async () => {
    const cents = Math.round(parseFloat(amount) * 100)
    if (!cents || cents <= 0) return

    setLoading(true)
    const result = await balanceService.topUpPspBalance({
      processor,
      payment_method,
      amount_cents: cents,
      note: note || undefined
    })
    setLoading(false)

    if (result) {
      setAmount("")
      setNote("")
      onSuccess()
      onClose()
    }
  }

  const handleClose = () => {
    setAmount("")
    setNote("")
    setSelectedProcessor("")
    setSelectedMethod("")
    setMethods([])
    onClose()
  }

  return (
    <Modal open={open} handler={handleClose}>
      <div className="relative flex flex-col gap-4 modal-box min-w-[400px]">
        <h3 className="text-lg font-semibold">
          {t("balance.topUp")}
          {!isSelectable && ` — ${processor} / ${payment_method}`}
        </h3>

        {isSelectable && (
          <>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">{t("balance.processor")}</label>
              <select
                value={selectedProcessor}
                onChange={(e) => setSelectedProcessor(e.target.value)}
                className="select select-bordered w-full"
              >
                <option value="">{t("balance.selectProcessor")}</option>
                {processors.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">{t("balance.paymentMethod")}</label>
              <select
                value={selectedMethod}
                onChange={(e) => setSelectedMethod(e.target.value)}
                className="select select-bordered w-full"
                disabled={!selectedProcessor}
              >
                <option value="">{t("balance.selectPaymentMethod")}</option>
                {methods.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t("balance.amountUsd")}</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="100.00"
            className="input input-bordered w-full"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t("balance.note")}</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("balance.noteOptional")}
            className="input input-bordered w-full"
          />
        </div>

        <div className="modal-action">
          <button className="btn btn-secondary" onClick={handleClose} disabled={loading}>
            {t("common.cancel")}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !amount || parseFloat(amount) <= 0 || !processor || !payment_method}
          >
            {loading ? t("common.loading") : t("balance.topUp")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
